import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, KeyboardAvoidingView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';

import { Button } from '@/components/ButtonComponent';
import { Picker } from '@/components/Picker';
import { ScrollableScreen } from '@/components/ScrollableScreen';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { failedKycCountSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useIsFirstScreenInStack } from '@/hooks/use-is-first-screen-in-stack';
import { useSheetKeyboardOffset } from '@/hooks/use-sheet-keyboard-offset';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { KycStackNavigationProp } from '@/navigation/kyc/types';
import { KycStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { isDobFirstAB } from '@/utils/ab-testing';
import { logger } from '@/utils/logging/index';
import { parse } from 'date-fns';

import { STATES, getKycFlowOrderedSteps, kycFlowTypes, kycRetryFlowOrderedSteps, kycRouteNames } from '../const';
import { useKycFieldsStore } from '../hooks/use-kyc-fields';
import { DobPicker } from './DobPicker';

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    action: {
        width: '100%',
        backgroundColor: 'inherit',
        height: 75,
        justifyContent: 'center',
    },
    datePickerContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        elevation: 5,
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
});

type KYCRoutes = Pick<KycStackParamList, (typeof kycRouteNames)[keyof typeof kycRouteNames]>;
type KycFlowTypes = (typeof kycFlowTypes)[keyof typeof kycFlowTypes];

type KycStepContainerProps = {
    title: string;
    subText: string;
    isNextDisabled: boolean;
    isNextBtnLoading?: boolean;
    kycFlowType?: KycFlowTypes;
    buttonText?: 'common:next' | 'kyc:verify_identity' | 'kyc:verify_identity_and_deposit' | 'common:save';
    headerTitle?: string;
    onPressButton?: () => void;
    hasCloseIcon?: boolean;
    showPicker?: boolean;
    showDobPicker?: boolean;
    setShowPicker?: (show: boolean) => void;
    setShowDobPicker?: (show: boolean) => void;
    pickedValue?: string;
    handlePickerChange?: (pickedValue: string) => void;
    handleDatePickerChange?: (pickedValue: Date) => void;
    onFormSubmit?: () => Promise<boolean>;
    errorMessage?: string;
    onCloseIconPress?: () => void;
    handleButtonPressTracking?: () => void;
    // flag indicating if the step is shown as a sheet
    sheet?: boolean;
};

type KycStepContainerPropsWithKycFieldHandler = PropsWithChildren<KycStepContainerProps>;

const defaultTitle = 'Sign Up';

export const KycStepContainer: React.FC<KycStepContainerPropsWithKycFieldHandler> = ({
    children,
    headerTitle = defaultTitle,
    title,
    subText,
    isNextDisabled,
    isNextBtnLoading,
    onPressButton,
    hasCloseIcon,
    buttonText = 'common:next',
    kycFlowType = kycFlowTypes.INITIAL,
    showPicker,
    showDobPicker,
    setShowPicker,
    setShowDobPicker,
    pickedValue = '',
    handlePickerChange,
    handleDatePickerChange,
    onFormSubmit,
    errorMessage,
    onCloseIconPress,
    handleButtonPressTracking,
    sheet = false,
}) => {
    const route = useRoute<RouteProp<KycStackParamList, keyof KYCRoutes>>();
    const { t } = useTranslation(['kyc', 'common']);
    const tSocialWallet = useSocialWalletTranslation(['kyc', 'common']);
    const navigation = useNavigation<KycStackNavigationProp>();
    const isFirstScreenInStack = useIsFirstScreenInStack();

    const { data: retryCount = 0 } = useAuthUserConfig({
        select: failedKycCountSelector,
    });

    const values = useKycFieldsStore(state => state.values);
    const date = values.dateOfBirth ? parse(values.dateOfBirth, 'MMddyyyy', new Date()) : new Date(1994, 0, 1);

    const navBarTitle = headerTitle === defaultTitle ? t('verify_identity') : headerTitle;
    const btnText = tSocialWallet(buttonText, { ns: ['kyc', 'common'] });
    const isDobFirst = isDobFirstAB();
    const isFirstAttempt = retryCount === 0;

    const flowSteps = {
        [kycFlowTypes.INITIAL]: getKycFlowOrderedSteps({ isDobFirst, isFirstAttempt }),
        [kycFlowTypes.RETRY]: kycRetryFlowOrderedSteps,
    };

    /* Get next route name on flow base on current route name (current screen) */
    const getNextRouteName = () => {
        const currentFlow = flowSteps[kycFlowType];
        const currentRoute = route.name;
        const routeNameIndex = currentFlow.findIndex(elm => elm === currentRoute);
        const nextRoute = currentFlow[routeNameIndex + 1];
        return nextRoute;
    };

    const handlePress = async () => {
        handleButtonPressTracking?.();
        try {
            const res = await onFormSubmit?.();
            if (res === false) {
                return; // return if submit response is false, don't navigate to next route
            }
        } catch (error) {
            logger.warn('[KYC form submit]', error);
            return; // return if error, don't navigate to next route
        }

        if (onPressButton) {
            onPressButton();
        } else {
            navigation.navigate(getNextRouteName());
        }
    };

    const isBtnLoading = isNextBtnLoading;

    const slideAnim = useRef(new Animated.Value(300)).current;

    const { keyboardVerticalOffset, onLayout: handleKeyboardAvoidingLayout } = useSheetKeyboardOffset();
    const { bottom: bottomInset } = useSafeAreaInsets();

    useEffect(() => {
        if (showDobPicker) {
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 300,
                useNativeDriver: true,
            }).start();
        } else {
            Animated.timing(slideAnim, {
                toValue: 300,
                duration: 300,
                useNativeDriver: true,
            }).start();
        }
    }, [showDobPicker, slideAnim]);

    return (
        <KeyboardAvoidingView
            behavior="padding"
            keyboardVerticalOffset={keyboardVerticalOffset - bottomInset}
            onLayout={handleKeyboardAvoidingLayout}
            style={common.flex}
        >
            <ScrollableScreen
                screenProps={{ edges: ['bottom', 'top'] }}
                footer={
                    <>
                        <View style={common.alignCenter}>
                            <Text
                                color={ds.colorToken.text.danger}
                                variant="bodySmall"
                                textAlign="center"
                                paddingHorizontal="s16"
                                style={common.justifyCenter}
                            >
                                {errorMessage}
                            </Text>
                        </View>
                        <View style={[styles.action, common.paddingHorizontal]}>
                            <Button
                                loading={isBtnLoading}
                                hierarchy={'primary'}
                                label={btnText}
                                disabled={isBtnLoading || isNextDisabled}
                                onPress={handlePress}
                                testID="kycNextButton"
                            />
                        </View>
                        {showPicker && handlePickerChange ? (
                            <Picker
                                items={STATES}
                                pickedValue={pickedValue}
                                closePicker={() => setShowPicker?.(false)}
                                handleValueChange={handlePickerChange}
                            />
                        ) : null}
                        <DobPicker
                            date={date}
                            showDobPicker={showDobPicker}
                            setShowDobPicker={setShowDobPicker}
                            handleDatePickerChange={handleDatePickerChange}
                        />
                    </>
                }
                screenNavBarProps={{
                    sheet,
                    closeIconMode: hasCloseIcon || isFirstScreenInStack ? 'close' : 'back',
                    title: hasCloseIcon ? '' : navBarTitle,
                    onClose: onCloseIconPress,
                }}
            >
                <Text lineHeight={32} fontSize={22} fontWeight="600" testID="kycTitle">
                    {title}
                </Text>
                <SizedBox value={4} />
                <Text lineHeight={24} fontSize={15} color={ds.colorToken.text.secondary} testID="kycSubText">
                    {subText}
                </Text>
                <SizedBox value={24} />
                {children}
            </ScrollableScreen>
        </KeyboardAvoidingView>
    );
};
